import { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import "@/App.css";
import { API } from "./lib/pos";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { cacheRef, drainQueue, getCachedRef, queueSize, readSyncedLog } from "./lib/offline";
import { Login } from "./components/Login";
import { Nav } from "./components/Nav";
import { Pos } from "./pages/Pos";
import { Kitchen } from "./pages/Kitchen";
import { Manager } from "./pages/Manager";
import { MenuBuilder } from "./pages/MenuBuilder";
import { CategoriesAdmin } from "./pages/CategoriesAdmin";
import { StationAdmin } from "./pages/StationAdmin";
import { DailySummary } from "./pages/DailySummary";
import { Reports } from "./pages/Reports";
import { UserAdmin } from "./pages/UserAdmin";
import { CompletedOrders } from "./pages/CompletedOrders";
import { RefundLedger } from "./pages/RefundLedger";
import { BillSettingsAdmin } from "./pages/BillSettingsAdmin";
import SmtpApi from "./pages/SmtpApi";
import OnlineCategoriesAdmin from "./pages/OnlineCategoriesAdmin";
import { DraftTickets, DraftsBanner } from "./pages/DraftTickets";
import { Customers } from "./pages/Customers";
import TokenDisplay from "./pages/TokenDisplay";
import ResetPassword from "./pages/ResetPassword";
import CustomerOrder from "./pages/CustomerOrder";

axios.defaults.withCredentials = true;

function Shell() {
  const { user } = useAuth();
  const isManager = user.role === "manager";
  const isCashier = user.role === "cashier";
  const hasModule = (m) => isManager || (isCashier && (user.modules || []).includes(m));

  // Default landing view is the Kitchen Display for every role that has it.
  const [view, setView] = useState(() => {
    if (isManager || isCashier) return "kitchen";
    return "kitchen";
  });
  const [posResetKey, setPosResetKey] = useState(0); // bumping this forces Pos to re-mount with a fresh cart
  const [menu, setMenu] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stations, setStations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [billSettings, setBillSettings] = useState(null);
  const [wsLive, setWsLive] = useState(false);
  const [wsPulse, setWsPulse] = useState(0);
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(queueSize());
  const [syncing, setSyncing] = useState(false);
  const [draftsCount, setDraftsCount] = useState(0);
  const [pendingDraft, setPendingDraft] = useState(null); // hydrate POS cart when a draft is opened

  const openDraftInPos = (draft) => {
    setPendingDraft(draft);
    setView("pos");
    setPosResetKey((k) => k + 1);
  };

  // Table management is disabled — no /api/tables fetch and no `tables` prop passed anywhere.
  const refresh = () => Promise.all([
    axios.get(`${API}/menu`).catch(() => ({ data: [] })),
    axios.get(`${API}/orders`).catch(() => ({ data: [] })),
    axios.get(`${API}/stations`).catch(() => ({ data: [] })),
    axios.get(`${API}/categories`).catch(() => ({ data: [] })),
    axios.get(`${API}/settings/bill`).catch(() => ({ data: null })),
  ]).then(([m, o, s, c, bs]) => {
    // Hydrate from cache when the network call returned an empty payload (i.e. we're offline).
    const cached = getCachedRef() || {};
    const menu = m.data.length ? m.data : (cached.menu || []);
    const orders = o.data.length ? o.data : (cached.orders || []);
    const stations = s.data.length ? s.data : (cached.stations || []);
    const categories = c.data.length ? c.data : (cached.categories || []);
    const bill = bs.data || cached.billSettings || null;
    setMenu(menu); setOrders(orders); setStations(stations); setCategories(categories);
    if (bill) setBillSettings(bill);
    // Warm the offline cache with the freshly-loaded reference data. Skip orders — they're
    // volatile and shouldn't be shown from cache.
    cacheRef({ menu, stations, categories, billSettings: bill });
  });

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Poll pending drafts for the sidebar badge — cashier + manager only.
  useEffect(() => {
    if (!(isManager || (user.role === "cashier"))) return;
    const load = () => axios.get(`${API}/drafts`).then((r) => setDraftsCount((r.data || []).length)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [isManager, user.role]);

  // Online/offline detection + auto-sync on reconnect.
  useEffect(() => {
    let pollT = null;
    const trySync = async () => {
      if (!navigator.onLine) return;
      if (queueSize() === 0) return;
      setSyncing(true);
      try {
        const r = await drainQueue();
        setPendingCount(r.remaining);
        if (r.synced > 0) refresh(); // pull the newly-persisted orders back into state
      } finally { setSyncing(false); }
    };
    const onOnline = () => { setOnline(true); trySync(); };
    const onOffline = () => setOnline(false); // keep offline; user keeps working from cache
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    // Poll every 5 s so an offline user who reconnects mid-shift gets pushed even if the
    // "online" event doesn't fire (some browsers under captive-portal conditions swallow it).
    pollT = setInterval(() => {
      const nowOnline = navigator.onLine;
      if (nowOnline !== online) setOnline(nowOnline);
      setPendingCount(queueSize());
      if (nowOnline) trySync();
    }, 5000);
    // Kick a first sync attempt on mount (if we came back online while the tab was closed).
    trySync();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      if (pollT) clearInterval(pollT);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Live WebSocket: push-based invalidations. Falls back to slow polling if disconnected.
  useEffect(() => {
    let ws = null;
    let closed = false;
    let backoff = 1000;
    let pollTimer = null;

    const startPollFallback = () => {
      if (pollTimer) return;
      pollTimer = setInterval(refresh, 15000);
    };
    const stopPollFallback = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const connect = () => {
      if (closed) return;
      try {
        const wsUrl = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws";
        ws = new WebSocket(wsUrl);
      } catch (e) {
        startPollFallback();
        return;
      }
      ws.onopen = () => { setWsLive(true); stopPollFallback(); backoff = 1000; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "invalidate") { refresh(); setWsPulse((n) => n + 1); }
        } catch (_) { /* ignore */ }
      };
      ws.onclose = () => {
        setWsLive(false);
        startPollFallback();
        if (!closed) { setTimeout(connect, Math.min(backoff, 15000)); backoff *= 2; }
      };
      ws.onerror = () => { try { ws.close(); } catch (_) { /* ignore */ } };
    };
    connect();
    return () => { closed = true; stopPollFallback(); try { ws && ws.close(); } catch (_) { /* ignore */ } };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // Intercept the "display" nav click — open the public token board in a new tab so
  // it can be projected on a customer monitor without leaving the operator's session.
  const handleSetView = (next) => {
    if (next === "display") {
      window.open("/display", "_blank", "noopener");
      return;
    }
    setView(next);
  };
  // "New Order" (top-right of every screen) jumps to POS with a freshly reset cart.
  const startNewOrder = () => { setPosResetKey((n) => n + 1); setView("pos"); };

  return (
    <div className="app-shell">
      <Nav view={view} setView={handleSetView} wsLive={wsLive} wsPulse={wsPulse} draftsCount={draftsCount} />
      <main>
        {(!online || pendingCount > 0) && (
          <div
            data-testid="offline-banner"
            style={{
              position: "fixed", top: 0, left: 0, right: 0, zIndex: 45,
              padding: "6px 14px", textAlign: "center", fontSize: 12, letterSpacing: 1,
              background: online ? "#295c22" : "#7a1c1c", color: "#fff",
              borderBottom: online ? "1px solid #3aa03a" : "1px solid #d94b4b",
              fontWeight: 600, textTransform: "uppercase",
            }}
            title={online ? "Reconnected — syncing queued orders" : "Working offline — new orders will sync when the connection returns"}
          >
            {online
              ? `${syncing ? "Syncing" : "Queued"} · ${pendingCount} offline order${pendingCount === 1 ? "" : "s"} awaiting sync`
              : `Offline mode · ${pendingCount} order${pendingCount === 1 ? "" : "s"} queued locally`}
          </div>
        )}
        {hasModule("pos") && (
          <button
            data-testid="new-order-button"
            onClick={startNewOrder}
            style={{
              position: "fixed", top: 20, right: 24, zIndex: 40,
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 14px", fontWeight: 700, letterSpacing: 0.4,
              background: "linear-gradient(180deg, #f5b342, #d68b1c)", color: "#1a1207",
              border: "1px solid #b17612", borderRadius: 8, cursor: "pointer",
              boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
            }}
            title="Start a new order in the POS"
          >
            <Plus size={16} />New order
          </button>
        )}
        {hasModule("pos") && view === "pos" && (
          <>
            <DraftsBanner onOpenInPos={openDraftInPos} />
            <Pos key={posResetKey} menu={menu} stations={stations} billSettings={billSettings} refresh={refresh} setView={setView} pendingDraft={pendingDraft} clearPendingDraft={() => setPendingDraft(null)} />
          </>
        )}
        {hasModule("pos") && view === "drafts" && <DraftTickets refresh={refresh} onOpenInPos={openDraftInPos} />}
        {view === "kitchen" && <Kitchen orders={orders} stations={stations} menu={menu} billSettings={billSettings} refresh={refresh} />}
        {hasModule("manager") && view === "manager" && <Manager orders={orders} stations={stations} />}
        {isManager && view === "customers" && <Customers />}
        {hasModule("menu") && view === "menu" && <MenuBuilder menu={menu} categories={categories} refresh={refresh} />}
        {hasModule("categories") && view === "categories" && <CategoriesAdmin stations={stations} refresh={refresh} />}
        {hasModule("stations") && view === "stations" && <StationAdmin stations={stations} categories={categories} refresh={refresh} />}
        {isManager && view === "users" && <UserAdmin />}
        {isManager && view === "settings" && <BillSettingsAdmin onSaved={refresh} />}
        {isManager && view === "smtp" && <SmtpApi />}
        {isManager && view === "online-categories" && <OnlineCategoriesAdmin />}
        {hasModule("summary") && view === "summary" && <DailySummary />}
        {hasModule("summary") && view === "completed" && <CompletedOrders stations={stations} billSettings={billSettings} />}
        {isManager && view === "refunds" && <RefundLedger />}
        {view === "reports" && <Reports stations={stations} />}
        <div className="app-copyright" data-testid="copyright-footer">
          Copyright {new Date().getFullYear()}: KKK Food Court Pvt Ltd
        </div>
      </main>
    </div>
  );
}


export default function App() {
  // Public routes — bypass auth entirely.
  if (typeof window !== "undefined") {
    const p = window.location.pathname;
    if (p === "/display") return <TokenDisplay />;
    if (p === "/order") return <CustomerOrder />;
    if (p === "/reset-password") return (
      <ThemeProvider><ResetPassword /></ThemeProvider>
    );
  }
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthGate />
      </AuthProvider>
    </ThemeProvider>
  );
}

function AuthGate() {
  const { user } = useAuth();
  if (user === null) return <div className="auth-page"><div className="auth-loading">Loading…</div></div>;
  if (!user) return <Login />;
  return <Shell />;
}
